import {
  delay, distinctUntilChanged, filter, fromEvent,
  map, merge, Subject, withLatestFrom
} from 'rxjs';
import { Disposable } from '..';
import { USER_KEYBOARD, USER_PANEL_CLOSED, USER_PANEL_OPENED } from '../../types';
import { TRANSITION_DURATION } from '../../ui/common/theme/colors';
import { getLastPanelUserEventStream, isTagUnique } from '../../utils';
import { BACK_BTN_SHORTCUTS } from '../shortcut/types';

export default class PanelController extends Disposable {
  constructor({
    userEvents$,
    playerConfig$,
    recommendationController: { shouldDisplay$, recommendations$ }
  }) {
    super();
    this.closePanel$ = new Subject();
    this.nextFocusedBtn$ = new Subject();
    this.keydown$ = new Subject();
    this.focusedPanel$ = new Subject();

    // Store last playerConfig value in instance
    playerConfig$.subscribe((cfg = {}) => { this.config = cfg; }).unsubscribe();

    PanelController.keyDown().subscribe(this.keydown$);

    const backMapping = !isTagUnique(this.config) ? [27] : BACK_BTN_SHORTCUTS.key;
    PanelController.shouldClosePanelStream({
      focusedPanel$: this.focusedPanel$,
      keydown$: this.keydown$,
      backMapping,
      userEvents$
    })
      .subscribe(this.closePanel$);

    PanelController.createFocusedPanel()
      .subscribe(this.focusedPanel$);

    PanelController.createFocusElementStream({ shouldDisplay$, recommendations$, userEvents$, isTV: isTagUnique(this.config) })
      .subscribe(this.nextFocusedBtn$);
  }

  static keyDown() {
    return fromEvent(document, 'keydown');
  }

  static shouldClosePanelStream({
    focusedPanel$,
    keydown$,
    backMapping,
    userEvents$
  }) {
    return merge(
      keydown$.pipe(
        withLatestFrom(focusedPanel$),
        filter(([{ keyCode, keyIdentifier, key: eventKey }, isPanelViewFocused]) => (
          !isPanelViewFocused
          || backMapping.includes(keyIdentifier)
          || backMapping.includes(eventKey)
          || backMapping.includes(keyCode))),
        map(() => true)
      ),
      userEvents$.pipe(
        filter(({ action }) => action === USER_PANEL_OPENED),
        map(() => false)
      )
    );
  }

  static createFocusedPanel() {
    return fromEvent(document, 'focusin').pipe(
      map((event) => [
        ...document.querySelectorAll('.ftv-magneto--panel')
      ].some((element) => element?.contains(event.target))),
      distinctUntilChanged()
    );
  }

  static createFocusElementStream({ shouldDisplay$, recommendations$, userEvents$, isTV }) {
    const lastClosedPanel$ = getLastPanelUserEventStream(userEvents$).pipe(
      filter(({ action, source }) => action === USER_PANEL_CLOSED && source === USER_KEYBOARD),
      map(({ value: { name } }) => name),
      delay(isTV ? 0 : TRANSITION_DURATION + 2)
    );

    const lastOpenPanel$ = getLastPanelUserEventStream(userEvents$).pipe(
      filter(({ action, source }) => action === USER_PANEL_OPENED && source === USER_KEYBOARD),
      map(() => 'btn-close-panel-live-options'),
      delay(isTV ? 0 : TRANSITION_DURATION + 2)
    );
    const recoTV$ = shouldDisplay$.pipe(
      filter(({ shouldDisplay }) => shouldDisplay),
      delay(300),
      withLatestFrom(recommendations$),
      map(([, recommendations]) => recommendations[0]?.src)
    );

    return merge(
      lastOpenPanel$,
      recoTV$,
      lastClosedPanel$
    );
  }
}
